/**
 * Customer Account Dashboard & Profile Page.
 * Dynamic user profile reading live orders, saved addresses, and wishlist items.
 */

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useCallback } from "react";
import {
  Package,
  MapPin,
  Heart,
  LogOut,
  ShieldCheck,
  ChevronRight,
  ShoppingBag,
  Lock,
  Plus,
  Trash2,
  Edit2,
  CheckCircle2,
} from "lucide-react";
import { BreadcrumbNav } from "@/components/common/breadcrumb-nav";
import { formatPrice } from "@/components/forms/price-display";
import { siteConfig } from "@/config/site";
import { useCurrentUser } from "@/hooks/use-auth";
import { LoadingSkeleton } from "@/components/common/loading-skeleton";
import { useCart } from "@/features/cart/store/cart-store";
import { useWishlist } from "@/features/wishlist/store/wishlist-store";
import {
  getUserOrders,
  fetchUserOrdersFromSupabase,
  getSavedAddress,
  saveAddress,
  type UserOrder,
  type SavedAddress,
} from "@/features/orders/store/orders-store";
import { OrderHistoryList } from "@/features/orders/components/order-history-list";

import { supabase } from "@/lib/supabase";

export const Route = createFileRoute("/account/")({
  head: () => ({
    meta: [
      { title: `My Account — ${siteConfig.name}` },
      // noindex,follow — see the note in account/login.tsx: crawl allowed, indexing refused.
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: AccountDashboard,
});

export function AccountDashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, isAdmin, signOut } = useCurrentUser();
  const { add } = useCart();
  const { wishlist, count: wishlistCount, removeWishlist } = useWishlist();

  const [orders, setOrders] = useState<UserOrder[]>([]);
  const [savedAddress, setSavedAddress] = useState<SavedAddress | null>(null);

  // Address editing state
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addrName, setAddrName] = useState("");
  const [addrStreet, setAddrStreet] = useState("");
  const [addrCity, setAddrCity] = useState("");
  const [addrState, setAddrState] = useState("");
  const [addrPincode, setAddrPincode] = useState("");
  const [addrPhone, setAddrPhone] = useState("");
  const [saveSuccessMsg, setSaveSuccessMsg] = useState<string | null>(null);

  const loadUserOrders = useCallback(() => {
    if (!user) return;
    fetchUserOrdersFromSupabase(user.id, user.email).then((remoteOrders) => {
      if (remoteOrders && remoteOrders.length > 0) {
        setOrders(remoteOrders);
      }
    });
  }, [user]);

  useEffect(() => {
    if (user) {
      // 1. Initial quick load from local cache
      const userOrders = getUserOrders(user.id, user.email, user.phone);
      if (userOrders.length > 0) {
        setOrders(userOrders);
      }

      // 2. Fetch live remote orders from Supabase
      loadUserOrders();

      const addr = getSavedAddress(user.id, user.email);
      if (addr) {
        setSavedAddress(addr);
        setAddrName(addr.fullName);
        setAddrStreet(addr.address);
        setAddrCity(addr.city);
        setAddrState(addr.state);
        setAddrPincode(addr.pincode);
        setAddrPhone(addr.phone);
      } else {
        setAddrName(user.name || "");
        setAddrPhone(user.phone || "");
      }

      // Realtime subscription for customer's orders
      const channelName = `user-orders-${user.id}-${Math.random().toString(36).substring(2, 9)}`;
      const channel = supabase
        .channel(channelName)
        .on("postgres_changes", { event: "*", schema: "public", table: "orders", filter: `user_id=eq.${user.id}` }, () => {
          loadUserOrders();
        })
        .subscribe();

      const handleLocalUpdate = () => {
        loadUserOrders();
      };

      window.addEventListener("orders_updated", handleLocalUpdate);
      window.addEventListener("storage", handleLocalUpdate);

      let bc: BroadcastChannel | null = null;
      try {
        bc = new BroadcastChannel("orders_sync");
        bc.onmessage = () => {
          loadUserOrders();
        };
      } catch {
        // Ignored
      }

      return () => {
        supabase.removeChannel(channel);
        window.removeEventListener("orders_updated", handleLocalUpdate);
        window.removeEventListener("storage", handleLocalUpdate);
        bc?.close();
      };
    }
  }, [user, loadUserOrders]);

  useEffect(() => {
    if (!isLoading && (!isAuthenticated || !user)) {
      navigate({ to: "/account/login" as never, replace: true });
    }
  }, [isLoading, isAuthenticated, user, navigate]);

  const handleSaveAddress = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    const updated: SavedAddress = {
      fullName: addrName.trim() || user.name || "Customer",
      address: addrStreet.trim(),
      city: addrCity.trim(),
      state: addrState.trim(),
      pincode: addrPincode.trim(),
      phone: addrPhone.trim() || user.phone || "",
    };

    saveAddress(user.id, updated);
    if (user.email) saveAddress(user.email, updated);

    setSavedAddress(updated);
    setIsEditingAddress(false);
    setSaveSuccessMsg("Shipping address saved successfully!");

    setTimeout(() => setSaveSuccessMsg(null), 3000);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate({ to: "/account/login" as never });
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (isLoading || !isAuthenticated || !user) {
    return (
      <div className="section-container py-32 max-w-4xl mx-auto">
        <LoadingSkeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="section-container pb-28 pt-24 md:pt-28 max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-6">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-secondary flex items-center justify-center text-primary font-display text-2xl uppercase">
            {user.name ? user.name.charAt(0) : "U"}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display text-2xl sm:text-3xl">
                {user.name || "Valued Customer"}
              </h1>
              {isAdmin && (
                <span className="bg-primary/10 text-primary border border-primary/20 text-[10px] px-2 py-0.5 rounded font-mono font-medium">
                  ADMIN
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{user.email || user.phone}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isAdmin && (
            <Link
              to="/admin"
              className="inline-flex min-h-11 items-center gap-2 bg-primary px-5 text-xs tracking-[0.16em] uppercase text-primary-foreground hover:opacity-90"
            >
              <ShieldCheck className="h-4 w-4" /> Admin Console
            </Link>
          )}

          <button
            onClick={handleSignOut}
            className="inline-flex min-h-11 items-center gap-1.5 border border-border px-4 text-xs tracking-[0.16em] uppercase hover:bg-secondary"
          >
            <LogOut className="h-4 w-4 text-muted-foreground" /> Sign Out
          </button>
        </div>
      </div>

      {/* Quick Navigation Cards */}
      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        <button
          type="button"
          onClick={() => scrollToSection("recent-orders-section")}
          className="border bg-card p-5 flex items-center justify-between rounded-sm text-left hover:border-primary transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <Package className="h-5 w-5 text-primary" strokeWidth={1.25} />
            <div>
              <p className="text-sm font-medium">Orders</p>
              <p className="text-[11px] text-muted-foreground">
                {orders.length} {orders.length === 1 ? "order" : "orders"} placed
              </p>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>

        <button
          type="button"
          onClick={() => scrollToSection("shipping-address-section")}
          className="border bg-card p-5 flex items-center justify-between rounded-sm text-left hover:border-primary transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <MapPin className="h-5 w-5 text-primary" strokeWidth={1.25} />
            <div>
              <p className="text-sm font-medium">Addresses</p>
              <p className="text-[11px] text-muted-foreground">
                {savedAddress ? "1 saved address" : "Add address"}
              </p>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>

        <button
          type="button"
          onClick={() => scrollToSection("wishlist-section")}
          className="border bg-card p-5 flex items-center justify-between rounded-sm text-left hover:border-primary transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <Heart className="h-5 w-5 text-primary" strokeWidth={1.25} />
            <div>
              <p className="text-sm font-medium">Wishlist</p>
              <p className="text-[11px] text-muted-foreground">
                {wishlistCount} {wishlistCount === 1 ? "saved item" : "saved items"}
              </p>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      {/* Recent Orders Section */}
      <div id="recent-orders-section" className="mt-12 border bg-card p-6">
        <div className="flex items-center justify-between border-b pb-4">
          <h2 className="font-display text-xl flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-primary" strokeWidth={1.25} />
            Recent Orders ({orders.length})
          </h2>
        </div>

        <OrderHistoryList orders={orders} />
      </div>

      {/* Wishlist Section */}
      <div id="wishlist-section" className="mt-8 border bg-card p-6">
        <div className="flex items-center justify-between border-b pb-4">
          <h2 className="font-display text-xl flex items-center gap-2">
            <Heart className="h-5 w-5 text-primary" strokeWidth={1.25} />
            My Wishlist ({wishlistCount})
          </h2>
          <Link to="/shop" className="text-xs text-primary hover:underline">
            Browse Catalogue
          </Link>
        </div>

        {wishlist.length === 0 ? (
          <div className="py-10 text-center">
            <Heart className="mx-auto h-9 w-9 text-muted-foreground/50" strokeWidth={1.25} />
            <p className="mt-3 font-display text-base">Your wishlist is empty</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Save your favorite botanical pieces by clicking the heart icon on any product.
            </p>
          </div>
        ) : (
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {wishlist.map((item) => (
              <div
                key={item.id}
                className="flex gap-4 border p-3 rounded bg-background items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="h-16 w-14 object-cover bg-stone rounded-xs"
                  />
                  <div>
                    <p className="text-xs font-semibold text-foreground">{item.name}</p>
                    <p className="text-[11px] text-muted-foreground">{item.category}</p>
                    <p className="text-xs font-display font-medium mt-1">
                      {formatPrice(item.price)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => add(item)}
                    className="flex min-h-9 items-center gap-1 bg-primary px-3 text-[11px] uppercase tracking-wider text-primary-foreground hover:opacity-90 rounded-xs"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add
                  </button>
                  <button
                    type="button"
                    onClick={() => removeWishlist(item.id)}
                    title="Remove from wishlist"
                    className="grid h-9 w-9 place-items-center text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Saved Address Section */}
      <div id="shipping-address-section" className="mt-8 border bg-card p-6">
        <div className="flex items-center justify-between border-b pb-4">
          <h2 className="font-display text-xl">Default Shipping Address</h2>
          <button
            type="button"
            onClick={() => setIsEditingAddress((prev) => !prev)}
            className="inline-flex items-center gap-1.5 text-xs text-primary font-medium hover:underline"
          >
            {isEditingAddress ? (
              "Cancel"
            ) : savedAddress ? (
              <>
                <Edit2 className="h-3.5 w-3.5" /> Edit Address
              </>
            ) : (
              <>
                <Plus className="h-3.5 w-3.5" /> Add Address
              </>
            )}
          </button>
        </div>

        {saveSuccessMsg && (
          <div className="mt-4 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-100 p-3 rounded border border-emerald-500/20">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            <span>{saveSuccessMsg}</span>
          </div>
        )}

        {isEditingAddress ? (
          <form onSubmit={handleSaveAddress} className="mt-5 space-y-4 max-w-lg">
            <div>
              <label htmlFor="addr-name" className="eyebrow block">
                Full Name
              </label>
              <input
                id="addr-name"
                type="text"
                required
                value={addrName}
                onChange={(e) => setAddrName(e.target.value)}
                placeholder="Jane Doe"
                className="mt-1.5 min-h-11 w-full border border-border bg-background px-3.5 text-xs outline-none focus:border-primary"
              />
            </div>

            <div>
              <label htmlFor="addr-street" className="eyebrow block">
                Street Address
              </label>
              <input
                id="addr-street"
                type="text"
                required
                value={addrStreet}
                onChange={(e) => setAddrStreet(e.target.value)}
                placeholder="742 Evergreen Terrace"
                className="mt-1.5 min-h-11 w-full border border-border bg-background px-3.5 text-xs outline-none focus:border-primary"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="addr-city" className="eyebrow block">
                  City
                </label>
                <input
                  id="addr-city"
                  type="text"
                  required
                  value={addrCity}
                  onChange={(e) => setAddrCity(e.target.value)}
                  placeholder="Mumbai"
                  className="mt-1.5 min-h-11 w-full border border-border bg-background px-3.5 text-xs outline-none focus:border-primary"
                />
              </div>

              <div>
                <label htmlFor="addr-state" className="eyebrow block">
                  State
                </label>
                <input
                  id="addr-state"
                  type="text"
                  required
                  value={addrState}
                  onChange={(e) => setAddrState(e.target.value)}
                  placeholder="Maharashtra"
                  className="mt-1.5 min-h-11 w-full border border-border bg-background px-3.5 text-xs outline-none focus:border-primary"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="addr-pincode" className="eyebrow block">
                  Pincode
                </label>
                <input
                  id="addr-pincode"
                  type="text"
                  required
                  value={addrPincode}
                  onChange={(e) => setAddrPincode(e.target.value)}
                  placeholder="400001"
                  className="mt-1.5 min-h-11 w-full border border-border bg-background px-3.5 text-xs outline-none focus:border-primary"
                />
              </div>

              <div>
                <label htmlFor="addr-phone" className="eyebrow block">
                  Phone Number
                </label>
                <input
                  id="addr-phone"
                  type="tel"
                  required
                  value={addrPhone}
                  onChange={(e) => setAddrPhone(e.target.value)}
                  placeholder="+91-9876543210"
                  className="mt-1.5 min-h-11 w-full border border-border bg-background px-3.5 text-xs outline-none focus:border-primary"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                className="flex min-h-11 items-center justify-center bg-primary px-6 text-xs uppercase tracking-wider text-primary-foreground hover:opacity-90"
              >
                Save Shipping Address
              </button>
              <button
                type="button"
                onClick={() => setIsEditingAddress(false)}
                className="flex min-h-11 items-center justify-center border border-border px-6 text-xs uppercase tracking-wider hover:bg-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : savedAddress ? (
          <div className="mt-4 text-xs leading-relaxed text-muted-foreground">
            <p className="font-medium text-foreground text-sm">{savedAddress.fullName}</p>
            <p>{savedAddress.address}</p>
            <p>
              {savedAddress.city}, {savedAddress.state} {savedAddress.pincode}
            </p>
            <p>Phone: {savedAddress.phone || user.phone || "Not provided"}</p>
          </div>
        ) : (
          <div className="mt-4 py-4 text-xs text-muted-foreground">
            <p className="mb-3">
              No saved shipping address yet. Add your default address below to use during checkout.
            </p>
            <button
              type="button"
              onClick={() => setIsEditingAddress(true)}
              className="inline-flex min-h-11 items-center gap-1.5 bg-primary px-5 text-xs uppercase tracking-wider text-primary-foreground hover:opacity-90"
            >
              <Plus className="h-4 w-4" /> Add Shipping Address
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
